import React, { useCallback, useEffect, useState } from 'react';

import styled from 'styled-components';

import { Button, SingleSelect, Table, TextArea, spacing } from '@sine/sine-ui-components';

import formatDate from '../../../helpers/formatDate';
import type { LineItem, PlanSelectOption } from '../../../services/billing/billing.types';
import { useBilling } from '../BillingProvider';
import { isTrial, mapLicenseTypeToSelectOption, mapSelectOptionToLicenseType } from '../billing.helpers';
import { CMSPlanOptions, newLicenceTypeOptions } from '../constants/billing.constants';
import { useCMSPlanData } from '../hooks/billing.hooks';
import PlanSelector from './PlanSelector';
import SiteDisplay from './SiteDisplay';

const StyledSiteCell = styled(Table.Cell)`
  padding-left: ${spacing(3)} !important;
`;

const RemoveButtonCell = styled(Table.Cell)`
  text-align: right;

  button {
    display: inline-block;
  }
`;

interface ManageModalRowProps {
  lineItem: LineItem;
  onPlanChange: (lineItem: LineItem, planOption: PlanSelectOption | undefined) => void;
  onRemove: (lineItem: LineItem) => void;
  onNoteChange: (lineItem: LineItem, note: string) => void;
  planOptionsForRow: Array<PlanSelectOption>;
}

export function ManualManageModalRow({
  lineItem,
  onPlanChange,
  planOptionsForRow,
  onRemove,
  onNoteChange,
}: ManageModalRowProps) {
  const { currentTeam } = useBilling();
  const { data: CMSPlanData } = useCMSPlanData(currentTeam.id);

  const [currentVmsPlan, setCurrentVmsPlan] = useState<PlanSelectOption | undefined>();
  const [addContractors, setAddContractors] = useState<boolean>(false);
  const [currentCMSPlan, setCurrentCMSPlan] = useState<string | undefined>();

  const SiteTableCell =
    !lineItem.site || ('isNew' in lineItem.site && lineItem.site.isNew) ? StyledSiteCell : Table.Cell;

  const cmsPlan = CMSPlanData?.currentPlanType;

  const updateCombinedPlan = useCallback(
    (vmsPlan: PlanSelectOption | undefined, contractors: boolean, cmsPlanType: string) => {
      const vmsPlanType = (vmsPlan?.value || vmsPlan?.type) as string;
      const combinedLicenseType = mapSelectOptionToLicenseType(
        vmsPlanType,
        contractors,
        cmsPlanType || CMSPlanData?.effectivePlanType || '',
      );

      const combinedOption: PlanSelectOption = {
        label: vmsPlan?.label || '',
        value: vmsPlanType,
        type: combinedLicenseType,
      };

      onPlanChange(lineItem, combinedOption);
    },
    [CMSPlanData?.effectivePlanType, lineItem, onPlanChange],
  );

  const handleVmsPlanChange = (value: PlanSelectOption | undefined) => {
    setCurrentVmsPlan(value);
    updateCombinedPlan(value, addContractors, currentCMSPlan || cmsPlan || '');
  };

  const handleCmsChange = (value?: boolean) => {
    const boolValue = !!value;
    setAddContractors(boolValue);
    updateCombinedPlan(currentVmsPlan, boolValue, currentCMSPlan || cmsPlan || '');
  };

  useEffect(() => {
    const licenseType = lineItem.selectedPlanOption?.type || lineItem.licenceType;
    if (!licenseType) return;

    const decomposed = mapLicenseTypeToSelectOption(licenseType);
    if (!decomposed) {
      setCurrentVmsPlan(newLicenceTypeOptions[0]);
      setCurrentCMSPlan(cmsPlan);
      return;
    }

    const vmsPlan = planOptionsForRow.find(
      (planOption: PlanSelectOption) => planOption.value === decomposed.vmsPlanType,
    );

    setAddContractors(decomposed.addContractors);
    setCurrentVmsPlan(vmsPlan ?? newLicenceTypeOptions[0]);

    const effectiveCMSPlan = CMSPlanData?.currentPlanType || decomposed.cmsPlanType;
    setCurrentCMSPlan(effectiveCMSPlan);
  }, [lineItem.id, CMSPlanData?.currentPlanType, planOptionsForRow]);

  useEffect(() => {
    if (!currentVmsPlan) return;

    const newCMSPlan = CMSPlanData?.currentPlanType;
    setCurrentCMSPlan(newCMSPlan);
  }, []);

  return (
    <Table.Row data-st-ext='table-row-site'>
      <SiteTableCell data-st-ext='cell-site-name'>
        <SiteDisplay planType={lineItem.selectedPlanOption?.type} site={lineItem.site} showLogo={true} />
      </SiteTableCell>
      <Table.Cell data-st-ext='cell-status'>
        {lineItem.licenceType && isTrial(lineItem.licenceType) ? 'Trial' : lineItem.id ? 'Ongoing' : 'Unlicensed'}
      </Table.Cell>
      <Table.Cell data-st-ext='cell-start-date'>
        {lineItem.startDate && lineItem.startDate !== 'N/A' ? formatDate(lineItem.startDate, 'dateOnly') : '-'}
      </Table.Cell>
      <Table.Cell data-st-ext='cell-end-date'>
        {lineItem.endDate && lineItem.endDate !== 'N/A' ? formatDate(lineItem.endDate, 'dateOnly') : '-'}
      </Table.Cell>
      <Table.Cell data-st-ext='cell-visitors-plan'>
        <PlanSelector
          disabled={false}
          options={planOptionsForRow}
          value={currentVmsPlan}
          onChange={handleVmsPlanChange}
          data-st-ext='select-visitors-plan'
        />
      </Table.Cell>
      <Table.Cell>
        {currentCMSPlan && (
          <SingleSelect
            disabled={false}
            data-st-ext='select-cms-plan'
            noSpaceReservation
            options={[
              { value: 'none', label: 'None' },
              {
                value: currentCMSPlan,
                label: CMSPlanOptions.find((option) => option.type === currentCMSPlan)?.name || currentCMSPlan,
              },
            ]}
            value={
              addContractors
                ? {
                    value: currentCMSPlan,
                    label: CMSPlanOptions.find((option) => option.type === currentCMSPlan)?.name || currentCMSPlan,
                  }
                : { value: 'none', label: 'None' }
            }
            onChange={(opt) => handleCmsChange(opt?.value !== 'none')}
          />
        )}
      </Table.Cell>
      <Table.Cell data-st-ext='cell-note'>
        <TextArea
          name='note'
          data-st-ext='input-note'
          value={lineItem.note || ''}
          onChange={(val) => onNoteChange(lineItem, val || '')}
          noSpaceReservation
          rows={1}
        />
      </Table.Cell>
      <RemoveButtonCell fitContent>
        <Button
          iconOnly
          icon='delete'
          size='sm'
          variant='tertiary'
          onClick={() => onRemove(lineItem)}
          data-st-ext='button-remove-license'
        />
      </RemoveButtonCell>
    </Table.Row>
  );
}
